import {
  DocumentExcelPane,
  DocumentPdfPane,
  DocumentViewer,
  type DocumentExcelPaneHighlight,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
} from '@aecom-ai-engineering/react-components';
import { Spinner } from '@consigli/facade';
import {
  useGetBlobQuery,
  useGetBlobReadUrlQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isExcelDocumentType } from '@/util/document-check';

import type { BidComparisonCitation } from './bid-comparison-types';

interface BidComparisonSourcePanelProps {
  citation: BidComparisonCitation;
  /** Small uppercase line shown above the page number and in the viewer header. */
  heading: string;
  /** Label for the primary text block (e.g. "Finding" for a bid, "Requirement" for the tender). */
  valueLabel: string;
  value: string;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export const BidComparisonSourcePanel: FC<BidComparisonSourcePanelProps> = ({
  citation,
  heading,
  valueLabel,
  value,
  onClose,
  onNext,
  onPrevious,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const citationPackageId = citation.documentPackageId ?? packageId;
  const isExcel = !!citation.fileName && isExcelDocumentType(citation.fileName);

  const { data: readUrlData, isError: isReadUrlError } = useGetBlobReadUrlQuery(
    { projectId, packageId: citationPackageId, blobId: citation.blobId },
    { skip: isExcel },
  );

  const { data: blob, isError: isBlobError } = useGetBlobQuery(
    { projectId, packageId: citationPackageId, blobId: citation.blobId },
    { skip: !isExcel },
  );

  const highlights = useMemo((): DocumentViewerHighlight[] | undefined => {
    if (citation.x0 == null || citation.y0 == null || citation.x1 == null || citation.y1 == null) {
      return undefined;
    }
    return [
      {
        x0: citation.x0,
        y0: citation.y0,
        x1: citation.x1,
        y1: citation.y1,
        pageNumber: citation.page,
      },
    ];
  }, [citation]);

  const excelHighlight = useMemo((): DocumentExcelPaneHighlight | undefined => {
    if (!citation.excelMetadata) return undefined;
    return {
      sheetName: citation.excelMetadata.sheetName,
      row: citation.excelMetadata.row ?? null,
      col: citation.excelMetadata.col ?? null,
    };
  }, [citation.excelMetadata]);

  const renderers = useMemo(
    () => ({
      excel: (props: DocumentPaneRenderProps) => (
        <DocumentExcelPane
          active={props.active}
          caption={props.caption}
          fileName=""
          highlight={excelHighlight}
          searchable
          src={props.src}
        />
      ),
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={citation.page}
          highlights={highlights}
          continuousScroll
        />
      ),
    }),
    [citation.page, excelHighlight, highlights],
  );

  const src = isExcel ? (blob?.downloadUrl ?? '') : (readUrlData?.readUrl ?? '');
  const isError = isExcel ? isBlobError : isReadUrlError;

  if (!src) {
    if (isError) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
        role="presentation"
        onClick={onClose}
      >
        <Spinner />
      </div>
    );
  }

  const locationLabel = isExcel
    ? citation.excelMetadata?.sheetName
    : t('bid-comparison.source-page', 'page {{page}}', { page: citation.page });

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={src}
      fileType={isExcel ? 'excel' : undefined}
      fileName={citation.fileName}
      title={citation.label ?? heading}
      showNavigationActions={onNext !== undefined || onPrevious !== undefined}
      onNext={onNext}
      onPrevious={onPrevious}
      nextDisabled={onNext === undefined}
      previousDisabled={onPrevious === undefined}
      showSidebar
      sidebarLabel={t('bid-comparison.source-details', 'Source details')}
      renderers={renderers}
    >
      <div className="flex flex-col gap-4 p-4 text-sm">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-day-neutral-subtle">
            {heading}
          </span>
          {locationLabel && (
            <span className="text-xs text-day-neutral-subtle">{locationLabel}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
            {valueLabel}
          </span>
          <span className="whitespace-pre-line text-base font-semibold text-day-neutral-dark">
            {value}
          </span>
        </div>

        {citation.passage && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
              {t('bid-comparison.extract-label', 'Extract')}
            </span>
            <span className="whitespace-pre-line text-sm text-day-neutral-dark">
              {citation.passage}
            </span>
          </div>
        )}
      </div>
    </DocumentViewer>
  );
};
